/**
 * ResourceUnavailableBanner (Story 32-4 — ADR-0004 §Failure-State Taxonomy,
 * `resource_unavailable` row)
 *
 * Persistent, NON-dismissible banner on the client detail page (every tab):
 * one row per integration whose mapped platform resource went away while the
 * OAuth token itself still works. Two escape hatches per row — "Remap"
 * (re-point at another resource via the 32-3 CatalogPickerDialog in remap
 * mode, no re-auth) and "Remove integration" (the existing confirmed
 * disconnect dialog). ADR-0004 Anti-Pattern 7: NO dismiss affordance of any
 * kind — the banner leaves the screen ONLY when the resource recovers
 * (auto-clear via the 31-24 catalog refresh + conditional 60s polling), the
 * integration is remapped, or it is removed.
 *
 * Data source: the SAME ["client-integrations", clientId] query
 * IntegrationsCard observes (one network fetch, two observers — never a
 * second endpoint). The queryFn THROWS on !ok (32-5 senior-review F1
 * precedent): a transient API error must never clear a persistent banner —
 * TanStack v5 retains the last successful data on refetch error, keeping the
 * banner AND its poll alive.
 *
 * Always mount this component — it renders no visible UI when no integration
 * is affected, and the mounted AnimatePresence container is what lets the
 * exit animation play on auto-clear (§4.6).
 *
 * Resource axis ONLY: `auth_failed` (dead token) belongs to Story 32-5's
 * TokenAuthFailedBanner. Stacking contract (AC #12): the token-axis banner
 * mounts ABOVE the ClientTabBar; this one sits BELOW it, above the tab
 * content card. The propagation layer guarantees an integration is never in
 * both states at once (auth_failed precedence).
 */

import { useState } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import { ArrowsLeftRight, Plugs, Warning } from "@phosphor-icons/react";
import { Button } from "@provenleap/ui";
import { apiGet, apiPost } from "../../lib/apiClient";
import { useAuth } from "../../stores/authStore";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";
import type { ClientIntegration } from "../clients/types";
import {
  CatalogPickerDialog,
  type CatalogPickerRemapTarget,
} from "./CatalogPickerDialog";
import {
  DisconnectIntegrationDialog,
  type DisconnectResult,
} from "./DisconnectIntegrationDialog";

// ============================================================================
// Pure helpers (exported for direct unit-testability — AC #14)
// ============================================================================

/**
 * AC #5 conditional polling (TanStack v5 function form): 60s while any
 * integration sits in `resource_unavailable`, off otherwise. Returning
 * `false` stops the timer; polling resumes automatically when data changes
 * the predicate back.
 */
export function resourceUnavailableRefetchInterval(
  data: readonly ClientIntegration[] | undefined
): number | false {
  return data?.some((i) => i.syncStatus === "resource_unavailable")
    ? 60_000
    : false;
}

/** Display name + logo from PICKER_PLATFORMS (trap #6: never a hardcoded provider list). */
function platformMeta(platform: string): {
  displayName: string;
  logoSrc: string | null;
} {
  const p = PICKER_PLATFORMS.find((pp) => pp.platform === platform);
  if (!p) return { displayName: platform, logoSrc: null };
  return { displayName: p.displayName, logoSrc: logoUrl(p.domain) };
}

// ============================================================================
// Component
// ============================================================================

interface ResourceUnavailableBannerProps {
  clientId: string;
  /** For the Remove dialog's confirmation copy. */
  clientName: string;
  showToast: (type: ToastType, message: string) => void;
}

interface RemapState {
  platform: string;
  target: CatalogPickerRemapTarget;
}

interface RemoveState {
  integrationId: string;
  platformName: string;
  accountName: string | null;
}

export function ResourceUnavailableBanner({
  clientId,
  clientName,
  showToast,
}: ResourceUnavailableBannerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = user?.role === "owner" || user?.role === "admin";

  const { data } = useQuery<ClientIntegration[]>({
    queryKey: ["client-integrations", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}/integrations`);
      if (!res.ok) {
        // Persistent-banner contract: throwing keeps the last successful
        // data (and the 60s poll) alive through transient API blips.
        throw new Error(`client-integrations fetch failed: ${res.status}`);
      }
      const json = (await res.json()) as { data: ClientIntegration[] };
      return json.data;
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      resourceUnavailableRefetchInterval(query.state.data),
  });

  const affected = (data ?? []).filter(
    (i) => i.syncStatus === "resource_unavailable"
  );

  // §4.6 lastValue pattern: dialog targets survive the close animation.
  const [remapOpen, setRemapOpen] = useState(false);
  const [remapState, setRemapState] = useState<RemapState | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeState, setRemoveState] = useState<RemoveState | null>(null);

  function openRemap(integration: ClientIntegration): void {
    const { displayName } = platformMeta(integration.type);
    setRemapState({
      platform: integration.type,
      target: {
        integrationId: integration.id,
        currentResourceId: integration.oauthGrantResourceId,
        label: integration.accountName ?? displayName,
      },
    });
    setRemapOpen(true);
  }

  function openRemove(integration: ClientIntegration): void {
    const { displayName } = platformMeta(integration.type);
    setRemoveState({
      integrationId: integration.id,
      platformName: displayName,
      accountName: integration.accountName,
    });
    setRemoveOpen(true);
  }

  /** Remap success (fired by useGrantCatalog.onIntegrationChange). */
  function handleRemapped(): void {
    void queryClient.invalidateQueries({
      queryKey: ["client-integrations", clientId],
    });
    void queryClient.invalidateQueries({
      queryKey: ["client-integrations-health", clientId],
    });
    void queryClient.invalidateQueries({ queryKey: ["integration-alerts"] });
  }

  /** AC #10: mirror IntegrationsCard.handleDisconnected's invalidation set. */
  async function handleDisconnected(result: DisconnectResult): Promise<void> {
    if (result.oauthTokenId) {
      await apiPost(
        `/api/notifications/dismiss-by-token/${result.oauthTokenId}`
      ).catch(() => {
        // Non-fatal — notifications will remain but are harmless
      });
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["client-integrations", clientId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["client-integrations-health", clientId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["client-connections", clientId],
      }),
      queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
      queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] }),
      queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] }),
      queryClient.invalidateQueries({ queryKey: ["notification-list"] }),
    ]);
  }

  const actionButtonClass =
    "shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all";
  const actionButtonBorder = { border: "0.5px solid var(--hairline)" };

  return (
    <>
      <AnimatePresence initial={false}>
        {affected.length > 0 && (
          <motion.div
            role="alert"
            data-testid="resource-unavailable-banner"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="mt-2 rounded-[22px] bg-amber-50 px-5 py-4 shadow-xs"
            style={{ border: "0.5px solid hsl(45 80% 70%)" }}
          >
            <div className="flex items-center gap-2">
              <Warning size={18} className="shrink-0 text-amber-600" />
              <p className="text-sm font-semibold text-foreground">
                Resource access lost
                {affected.length > 1 ? ` (${affected.length})` : ""}
              </p>
            </div>

            <div className="mt-2 space-y-2">
              {affected.map((integration, index) => {
                const { displayName, logoSrc } = platformMeta(integration.type);
                const accountLabel = integration.accountName ?? displayName;
                return (
                  <motion.div
                    key={integration.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * (index + 1) }}
                    className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
                    data-testid={`resource-unavailable-row-${integration.id}`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {logoSrc && (
                        <img
                          src={logoSrc}
                          alt=""
                          className="h-4 w-4 shrink-0 rounded-sm"
                        />
                      )}
                      <p className="min-w-0 text-[13px] text-foreground">
                        <span className="font-semibold">{accountLabel}</span> (
                        {displayName}) is no longer accessible — the account may
                        have been removed or your access revoked on the platform
                        side.
                      </p>
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          aria-label={`Remap ${accountLabel}`}
                          className={actionButtonClass}
                          style={actionButtonBorder}
                          onClick={() => openRemap(integration)}
                        >
                          <ArrowsLeftRight size={16} className="mr-1.5" />
                          Remap
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          aria-label={`Remove ${accountLabel} integration`}
                          className={`${actionButtonClass} text-destructive hover:text-destructive`}
                          style={actionButtonBorder}
                          onClick={() => openRemove(integration)}
                        >
                          <Plugs size={16} className="mr-1.5" />
                          Remove integration
                        </Button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always-mounted dialogs (§4.6) — lastValue state keeps props stable
          through the close animation. */}
      <CatalogPickerDialog
        open={remapOpen}
        onOpenChange={setRemapOpen}
        platform={remapState?.platform ?? ""}
        defaultClientId={clientId}
        showToast={showToast}
        onAttached={handleRemapped}
        remapTarget={remapState?.target ?? null}
      />
      <DisconnectIntegrationDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        integrationId={removeState?.integrationId ?? ""}
        platformName={removeState?.platformName ?? ""}
        accountName={removeState?.accountName ?? null}
        clientName={clientName}
        showToast={showToast}
        onDisconnected={(result) => void handleDisconnected(result)}
      />
    </>
  );
}
